import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Home,
  Building2,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Google Maps Autocomplete Script Loader
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('Google Maps API key not configured'));
      return;
    }
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

// Address Autocomplete Component for Mutui - matches Clients.js styling
const AddressAutocompleteInput = ({ value, onChange, placeholder, inputRef }) => {
  const [autocomplete, setAutocomplete] = useState(null);
  const [inputValue, setInputValue] = useState(value || '');

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  useEffect(() => {
    if (!inputRef.current) return;

    loadGoogleMapsScript().then((google) => {
      // Clear any existing listeners
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }

      const options = {
        fields: ['address_components', 'formatted_address', 'geometry'],
        componentRestrictions: { country: 'it' },
        types: ['address']
      };

      const autoCompleteInstance = new google.maps.places.Autocomplete(inputRef.current, options);
      setAutocomplete(autoCompleteInstance);

      // CRITICAL: Fix pac-container z-index for Dialog compatibility
      const fixPacContainer = () => {
        const pacContainers = document.querySelectorAll('.pac-container');
        pacContainers.forEach(container => {
          container.style.zIndex = '99999';
          container.style.pointerEvents = 'auto';
          
          // Prevent modal close when clicking on dropdown
          const preventModalClose = (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
          };
          
          container.removeEventListener('mousedown', preventModalClose, true);
          container.removeEventListener('click', preventModalClose, true);
          container.removeEventListener('touchstart', preventModalClose, true);
          
          container.addEventListener('mousedown', preventModalClose, true);
          container.addEventListener('click', preventModalClose, true);
          container.addEventListener('touchstart', preventModalClose, true);
        });
      };

      // Fix z-index on focus and input
      const handleFocus = () => {
        setTimeout(fixPacContainer, 50);
        setTimeout(fixPacContainer, 200);
        setTimeout(fixPacContainer, 500);
      };

      const handleInput = () => {
        setTimeout(fixPacContainer, 50);
        setTimeout(fixPacContainer, 200);
      };

      inputRef.current.addEventListener('focus', handleFocus);
      inputRef.current.addEventListener('input', handleInput);

      // Initial fix
      setTimeout(fixPacContainer, 300);

      // Listen for place selection
      autoCompleteInstance.addListener('place_changed', () => {
        const place = autoCompleteInstance.getPlace();
        if (place.formatted_address) {
          setInputValue(place.formatted_address);
          onChange(place.formatted_address);
        }
      });

      return () => {
        if (window.google?.maps?.event) {
          google.maps.event.clearInstanceListeners(autoCompleteInstance);
        }
        if (inputRef.current) {
          inputRef.current.removeEventListener('focus', handleFocus);
          inputRef.current.removeEventListener('input', handleInput);
        }
      };
    }).catch(err => {
      console.error('Errore caricamento Google Maps:', err);
    });
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submit while selecting
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      data-testid="indirizzo-autocomplete"
    />
  );
};

const Mutui = () => {
  const [mutui, setMutui] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStato, setFilterStato] = useState('all');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMutuo, setEditingMutuo] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mutuoToDelete, setMutuoToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    indirizzo: '',
    prezzo_immobile: '',
    anticipo: '',
    importo_richiesto: '',
    durata_anni: '',
    banca: '',
    note: '',
    stato: 'analisi_preliminare'
  });
  
  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // Google Maps Autocomplete ref
  const addressInputRef = useRef(null);

  // Stati della pratica con colori
  const statoConfig = {
    analisi_preliminare: { label: 'Analisi Preliminare', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    inviata_banca: { label: 'Inviata in Banca', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    erogata: { label: 'Erogata', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    rifiutata: { label: 'Rifiutata', color: 'bg-red-100 text-red-800 border-red-300' }
  };

  const fetchMutui = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/mutui`);
      setMutui(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei mutui/prestiti');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei clienti:', error);
    }
  }, []);

  useEffect(() => {
    fetchMutui();
    fetchClients();
  }, [fetchMutui, fetchClients]);

  const resetForm = () => {
    setFormData({
      client_id: '',
      indirizzo: '',
      prezzo_immobile: '',
      anticipo: '',
      importo_richiesto: '',
      durata_anni: '',
      banca: '',
      note: '',
      stato: 'analisi_preliminare'
    });
    setClientSearch('');
    setEditingMutuo(null);
  };

  const openNewModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (mutuo) => {
    setEditingMutuo(mutuo);
    setFormData({
      client_id: mutuo.client_id,
      indirizzo: mutuo.indirizzo || '',
      prezzo_immobile: mutuo.prezzo_immobile || '',
      anticipo: mutuo.anticipo || '',
      importo_richiesto: mutuo.importo_richiesto || '',
      durata_anni: mutuo.durata_anni || '',
      banca: mutuo.banca || '',
      note: mutuo.note || '',
      stato: mutuo.stato
    });
    const client = clients.find(c => c.id === mutuo.client_id);
    if (client) {
      setClientSearch(`${client.last_name} ${client.first_name}`);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error('Seleziona un cliente');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        prezzo_immobile: formData.prezzo_immobile ? parseFloat(formData.prezzo_immobile) : null,
        anticipo: formData.anticipo ? parseFloat(formData.anticipo) : null,
        importo_richiesto: formData.importo_richiesto ? parseFloat(formData.importo_richiesto) : null,
        durata_anni: formData.durata_anni ? parseInt(formData.durata_anni) : null
      };
      
      if (editingMutuo) {
        await axios.put(`${API}/mutui/${editingMutuo.id}`, payload);
        toast.success('Mutuo/prestito aggiornato con successo');
      } else {
        await axios.post(`${API}/mutui`, payload);
        toast.success('Richiesta mutuo/prestito creata con successo');
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchMutui();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!mutuoToDelete) return;
    
    try {
      await axios.delete(`${API}/mutui/${mutuoToDelete.id}`);
      toast.success('Mutuo/prestito eliminato');
      setDeleteDialogOpen(false);
      setMutuoToDelete(null);
      fetchMutui();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const selectClient = (client) => {
    setFormData(prev => ({ ...prev, client_id: client.id }));
    setClientSearch(`${client.last_name} ${client.first_name}`);
    setShowClientDropdown(false);
  };

  const filteredClients = clients.filter(client => {
    const fullName = `${client.last_name} ${client.first_name}`.toLowerCase();
    return fullName.includes(clientSearch.toLowerCase());
  });

  const filteredMutui = mutui.filter(mutuo => {
    const matchesSearch = !searchQuery || 
      mutuo.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mutuo.banca?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mutuo.indirizzo?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStato = filterStato === 'all' || mutuo.stato === filterStato;
    
    return matchesSearch && matchesStato;
  });

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="mutui-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Home className="w-7 h-7 text-pink-600" />
            Mutui/Prestiti
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestione richieste di mutuo/prestito</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchMutui} data-testid="refresh-mutui">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
          <Button onClick={openNewModal} className="bg-pink-600 hover:bg-pink-700" data-testid="new-mutuo-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Richiesta
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Cerca per cliente, banca o indirizzo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-mutui"
          />
        </div>
        <Select value={filterStato} onValueChange={setFilterStato}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-stato">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="analisi_preliminare">Analisi Preliminare</SelectItem>
            <SelectItem value="inviata_banca">Inviata in Banca</SelectItem>
            <SelectItem value="erogata">Erogata</SelectItem>
            <SelectItem value="rifiutata">Rifiutata</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-slate-500">Totale Richieste</p>
          <p className="text-2xl font-bold text-slate-900">{mutui.length}</p>
        </div>
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
          <p className="text-sm text-amber-700">In Analisi</p>
          <p className="text-2xl font-bold text-amber-800">{mutui.filter(m => m.stato === 'analisi_preliminare').length}</p>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <p className="text-sm text-blue-700">Inviate in Banca</p>
          <p className="text-2xl font-bold text-blue-800">{mutui.filter(m => m.stato === 'inviata_banca').length}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
          <p className="text-sm text-emerald-700">Erogate</p>
          <p className="text-2xl font-bold text-emerald-800">{mutui.filter(m => m.stato === 'erogata').length}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Cliente</TableHead>
              <TableHead className="font-semibold">Data Richiesta</TableHead>
              <TableHead className="font-semibold">Importo Richiesto</TableHead>
              <TableHead className="font-semibold">Banca</TableHead>
              <TableHead className="font-semibold">Stato</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  Caricamento...
                </TableCell>
              </TableRow>
            ) : filteredMutui.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  Nessun mutuo/prestito trovato
                </TableCell>
              </TableRow>
            ) : (
              filteredMutui.map((mutuo) => (
                <TableRow key={mutuo.id} className="hover:bg-slate-50" data-testid={`mutuo-row-${mutuo.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{mutuo.client_name}</p>
                      <p className="text-xs text-slate-500">{mutuo.client_code}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {formatDate(mutuo.created_at)}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {formatCurrency(mutuo.importo_richiesto)}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {mutuo.banca || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline" 
                      className={statoConfig[mutuo.stato]?.color || 'bg-slate-100'}
                    >
                      {statoConfig[mutuo.stato]?.label || mutuo.stato}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`mutuo-actions-${mutuo.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditModal(mutuo)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Modifica
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => { setMutuoToDelete(mutuo); setDeleteDialogOpen(true); }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { setIsModalOpen(open); if (!open) resetForm(); }}>
        <DialogContent 
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // Prevent modal close when clicking on Google Maps autocomplete dropdown
            const target = e.target;
            if (target.closest('.pac-container') || target.classList?.contains('pac-item') || target.closest('.pac-item')) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            // Also block pointer down events on pac-container
            const target = e.target;
            if (target.closest('.pac-container') || target.classList?.contains('pac-item') || target.closest('.pac-item')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-pink-600" />
              {editingMutuo ? 'Modifica Mutuo/Prestito' : 'Nuova Richiesta Mutuo/Prestito'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Cliente (Obbligatorio) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Cliente *</Label>
              <div className="relative">
                <Input
                  placeholder="Cerca cliente..."
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setShowClientDropdown(true);
                    if (!e.target.value) setFormData(prev => ({ ...prev, client_id: '' }));
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  data-testid="client-search-input"
                />
                {showClientDropdown && clientSearch && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500">Nessun cliente trovato</div>
                    ) : (
                      filteredClients.slice(0, 10).map(client => (
                        <div
                          key={client.id}
                          className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => selectClient(client)}
                        >
                          <p className="font-medium">{client.last_name} {client.first_name}</p>
                          <p className="text-xs text-slate-500">{client.fiscal_code}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {formData.client_id && (
                <p className="text-xs text-emerald-600">Cliente selezionato</p>
              )}
            </div>

            {/* Indirizzo Immobile con Google Maps Autocomplete */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Indirizzo Immobile</Label>
              <AddressAutocompleteInput
                inputRef={addressInputRef}
                value={formData.indirizzo}
                onChange={(newAddress) => setFormData(prev => ({ ...prev, indirizzo: newAddress }))}
                placeholder="Inizia a digitare l'indirizzo..."
              />
              <p className="text-xs text-slate-500">Inizia a digitare per l'autocompletamento Google Maps</p>
            </div>

            {/* Campi Numerici - Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Prezzo Immobile (€)</Label>
                <Input
                  type="number"
                  placeholder="es. 250000"
                  value={formData.prezzo_immobile}
                  onChange={(e) => setFormData(prev => ({ ...prev, prezzo_immobile: e.target.value }))}
                  data-testid="prezzo-immobile-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Anticipo / Acconto (€)</Label>
                <Input
                  type="number"
                  placeholder="es. 50000"
                  value={formData.anticipo}
                  onChange={(e) => setFormData(prev => ({ ...prev, anticipo: e.target.value }))}
                  data-testid="anticipo-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Importo Richiesto (€)</Label>
                <Input
                  type="number"
                  placeholder="es. 200000"
                  value={formData.importo_richiesto}
                  onChange={(e) => setFormData(prev => ({ ...prev, importo_richiesto: e.target.value }))}
                  data-testid="importo-richiesto-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Durata (Anni)</Label>
                <Select 
                  value={formData.durata_anni?.toString() || ''} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, durata_anni: value }))}
                >
                  <SelectTrigger data-testid="durata-select">
                    <SelectValue placeholder="Seleziona durata" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 25, 30, 35, 40].map(anni => (
                      <SelectItem key={anni} value={anni.toString()}>{anni} anni</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Banca */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Banca</Label>
              <Input
                placeholder="es. Intesa Sanpaolo, UniCredit..."
                value={formData.banca}
                onChange={(e) => setFormData(prev => ({ ...prev, banca: e.target.value }))}
                data-testid="banca-input"
              />
            </div>

            {/* Stato Pratica */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Stato Pratica</Label>
              <Select 
                value={formData.stato} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, stato: value }))}
              >
                <SelectTrigger data-testid="stato-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="analisi_preliminare">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      Analisi Preliminare
                    </span>
                  </SelectItem>
                  <SelectItem value="inviata_banca">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      Inviata in Banca
                    </span>
                  </SelectItem>
                  <SelectItem value="erogata">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      Erogata
                    </span>
                  </SelectItem>
                  <SelectItem value="rifiutata">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      Rifiutata
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Note</Label>
              <Textarea
                placeholder="Dettagli aggiuntivi o motivi del rifiuto..."
                value={formData.note}
                onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                rows={3}
                data-testid="note-textarea"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setIsModalOpen(false); resetForm(); }}>
                Annulla
              </Button>
              <Button type="submit" className="bg-pink-600 hover:bg-pink-700" data-testid="save-mutuo-btn">
                {editingMutuo ? 'Salva Modifiche' : 'Crea Richiesta'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa richiesta di mutuo/prestito per <strong>{mutuoToDelete?.client_name}</strong>?
              <br />Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Mutui;
